import React, { useState, useCallback, useMemo } from 'react';
import { useReportsQuery } from '~/lib/Queries';
import Loading from '~/components/Loading';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import useBusinessDeleteApi from '~/lib/useBusinessDeleteApi';
import useBusinessPutApi from '~/lib/useBusinessPutApi';
import TStrings from './Reports.i18n.js';
import ReportRow from './ReportRow';
import { Report, ReportType, TemplateIndex } from './Report.types';
import ReportTemplateModal from './ReportTemplateModal';
import useBusinessNavigate from '~/lib/useBusinessNavigate';
import { createNewReportConfigForTemplate } from './Reports.utils.js';
import FeatureIntro from '~/components/FeatureIntro';
import PageHeading from '~/components/PageHeading';

const REPORT_TYPE_LABELS: Record<string, string> = {
    [ReportType.Clients]: 'Clients',
    [ReportType.Forms]: 'Forms',
    [ReportType.PackageGrants]: 'Packages',
    [ReportType.Events]: 'Events',
    [ReportType.Classes]: 'Classes',
    [ReportType.Payments]: 'Payments',
    [ReportType.GiftCertificates]: 'Gift Certificates',
    [ReportType.Invoices]: 'Invoices',
};

const ReportsIndexPage = () => {
    const [isEditReportModalShowing, setIsEditReportModalShowing] =
        useState(false);
    const [editedReport, setEditedReport] = useState<Report | null>(null);
    const { save, isSaving } = useBusinessPostApi(`/reports`);
    const { deleteRequest, isDeleting } = useBusinessDeleteApi(`/reports`);
    const { update, isUpdating } = useBusinessPutApi(`/reports`);
    const { isLoading, data: reports, refetch } = useReportsQuery();
    const navigate = useBusinessNavigate();

    const goToReportDetailsPage = useCallback(
        async (
            report: Report,
            navigateToDetails: boolean,
            templateSelected?: TemplateIndex,
        ) => {
            if (report.id) {
                setIsEditReportModalShowing(false);
                await refetch();
                if (navigateToDetails) {
                    navigate(`/reports/${report.id}`, {
                        state: { report, templateSelected },
                    }); // go to report details page
                }
                setEditedReport(null); // clear potential edited reports
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const onReportCreateClick = useCallback(
        async (templateSelected: TemplateIndex, aReport?: Report) => {
            const newReportConfig = createNewReportConfigForTemplate(
                templateSelected,
                aReport,
            );
            if (
                !newReportConfig ||
                newReportConfig?.settings?.templateSelected == null
            ) {
                console.error(
                    `Failed to create new report config, for template: ${templateSelected}`,
                    aReport,
                    newReportConfig?.settings?.templateSelected,
                );
            }
            const createdReport = await save(newReportConfig);
            await goToReportDetailsPage(createdReport, true, templateSelected);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const onReportClick = useCallback(async (aReport: Report) => {
        if (aReport.id) {
            await goToReportDetailsPage(aReport, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onReportEditClick = useCallback(async (aReport: Report) => {
        if (aReport.id) {
            const updatedReport = await update(aReport.id, {
                name: aReport?.name,
                settings: aReport?.settings,
            });
            await goToReportDetailsPage(updatedReport, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onReportDeleteClick = useCallback(async (aReport: Report) => {
        if (aReport.id) {
            await deleteRequest(aReport.id);
            await refetch();
            clearReports();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onReportDuplicateClick = useCallback(async (aReport: Report) => {
        if (aReport.id) {
            const duplicatedReport = {
                name: `${aReport.name} (Copy)`,
                type: aReport.type,
                settings: aReport.settings,
            };
            await save(duplicatedReport);
            await refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onReportNameEditClick = useCallback((aReport: Report) => {
        if (aReport.id) {
            setIsEditReportModalShowing(true);
            setEditedReport(aReport);
        }
    }, []);

    const clearReports = useCallback(() => {
        setIsEditReportModalShowing(false);
        setEditedReport(null); // clear potential edited reports
    }, []);

    const showReportModal = useCallback(() => {
        setIsEditReportModalShowing(true);
        setEditedReport(null); // clear potential edited reports
    }, []);

    const shouldGroup = (reports?.length ?? 0) > 9;

    const reportsByType = useMemo(() => {
        if (!reports) return {};
        return Object.groupBy(reports, (report: Report) =>
            shouldGroup ? (report.type || '') : '',
        );
    }, [reports, shouldGroup]);

    const renderReportRow = (report: Report) => (
        <ReportRow
            key={report.id}
            report={report}
            deleteReport={onReportDeleteClick}
            duplicateReport={onReportDuplicateClick}
            onReportClicked={onReportClick}
            editReportName={onReportNameEditClick}
            isDuplicating={isSaving}
            isDeleting={isDeleting}
        />
    );

    return (
        <div>
            <PageHeading
                heading="Reports"
                rightContent={
                    <button
                        type="button"
                        className="btn-pill text-nowrap ml-4"
                        onClick={showReportModal}
                    >
                        {TStrings.CREATE_REPORT_BUTTON}
                    </button>
                }
            />
            <FeatureIntro>
                Create reports to see information like your bookings from last
                month, client details, revenue per event type, and much more.
            </FeatureIntro>
            {isLoading && <Loading className="py-3" />}
            {Object.keys(reportsByType)
                .sort()
                .map(type => {
                    const groupReports = reportsByType[type] ?? [];
                    return (
                        <div key={type} className="mb-6">
                            {type && (
                                <h3 className="text-xl px-6 mb-2">
                                    {REPORT_TYPE_LABELS[type] || type}
                                </h3>
                            )}
                            <div className="max-w-2xl w-full panel pl-4 divide-y">
                                {groupReports.map(renderReportRow)}
                            </div>
                        </div>
                    );
                })}
            {reports?.length === 0 && !isLoading && (
                <div className="max-w-2xl w-full panel pl-4">
                    <div className="py-4">
                        <div>{TStrings.NO_REPORTS_MSG}</div>
                    </div>
                </div>
            )}
            <ReportTemplateModal
                visible={isEditReportModalShowing}
                onClose={clearReports}
                onNewReport={onReportCreateClick}
                onEditReport={onReportEditClick}
                onDeleteReport={onReportDeleteClick}
                isLoading={isSaving || isDeleting || isUpdating}
                reportToEdit={editedReport}
            />
        </div>
    );
};

export default ReportsIndexPage;
